import {
  Component,
  Input,
  ChangeDetectionStrategy,
  OnInit,
  ViewEncapsulation,
  Directive,
  TemplateRef,
  ContentChildren,
  QueryList,
  ChangeDetectorRef,
  OnDestroy,
  Output,
  EventEmitter,
  SimpleChanges,
  OnChanges, AfterViewInit, HostListener, ElementRef, ViewChild, Renderer2
} from '@angular/core';
import {DecimalPipe} from '@angular/common';
import {BehaviorSubject, isObservable, Observable, Subscription} from 'rxjs';
import {CloFilterTableService} from './clo-filter-table.service';
import {FormControl} from '@angular/forms';
import {filter} from 'rxjs/operators';
import {stringify} from 'querystring';

interface Filter {
  value: any;
  title: string;
  attribute: string;
}
interface SortBy {
  attributeName: string;
  direction: 'ASC' | 'DESC';
}

/**
 * The row template needed to be visible for a tablet view
 */
@Directive({
  selector: 'ng-template[clo-table-row]'
})
export class CloTableRow {
  @Input() filter_by: string;
  hidden = false;
  @Input() header = false;

  @Output() selected = new EventEmitter();
  constructor(public templateRef: TemplateRef<any>) {}

  selectRow(e) {
    this.selected.emit(e);
  }
}

/**
 * The card template needed to be visible for a tablet view
 */
@Directive({
  selector: 'ng-template[clo-tablet-card]'
})
export class CloTabletCard {
  @Input() filter_by: string;
  hidden = false;
  @Output() onClick = new EventEmitter();
  constructor(public templateRef: TemplateRef<any>) {}
  OnClick(event) {
    this.onClick.emit(event);
  }
}

/**
 * The card template needed to be visible for a mobile view
 */
@Directive({
  selector: 'ng-template[clo-mobile-card]'
})
export class CloMobileCard {
  @Input() filter_by: string;
  hidden = false;
  @Output() onClick = new EventEmitter();
  constructor(public templateRef: TemplateRef<any>) {}
  MobileOnClick(event) {
    this.onClick.emit(event);
  }
}

/**
 * Data tables display information in a way that’s easy to scan, so that users can look for patterns and insights.
 *
 * It can be filtered and searched
 */
@Component({
  selector: 'clo-table',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  providers: [DecimalPipe],
  host: {},
  templateUrl: 'table.html',
  styleUrls: ['./table.scss']
})

export class CloTable implements OnInit, OnDestroy, AfterViewInit, OnChanges {


  observedObjects$: Observable<Object>;
  filteredObjects: Array<Object> = [];

  /**
   * When using the CloTableRow instead of iterating through an array of objects.
   * Default: false;
   */
  @Input() objectsDataOnly = false;

  /**
   * This is used when describing the content in the table.  Ex: 'Users', 'Quotes', etc.
   * Default: 'Results'
   */
  @Input() objectTypeName = 'Results';

  /**
   * This is required if connecting to a CloTableBlock
   */
  @Input() tableId: string;
  // tableId: string;

  @Input() isList = false;


  // @Input() objects: Object[];

  /**
   * The headers for the table
   */
  @Input() tableHeaders: string[];

  /**
   * Is the Table sortable
   */
  @Input() sortable = true;

  @Input() initialSort: SortBy;

  /**
   * This returns an array of filterd objects
   */
  @Output() filteredResults = new EventEmitter();

  /**
   * When using the data to generate the rows this will be the click event
   */
  @Output() rowClicked = new EventEmitter();


  @ContentChildren(CloTableRow, {descendants: false}) tableRows: QueryList<CloTableRow>;
  private allPossibleRows: Array<CloTableRow>;

  @ContentChildren(CloMobileCard, {descendants: false}) mobileCards: QueryList<CloMobileCard>;
  @ContentChildren(CloTabletCard, {descendants: false}) tabletCards: QueryList<CloTabletCard>;

  @ViewChild('tableHeader', {static: false}) tableHeader: ElementRef;
  @ViewChild('table', {static: false}) table: ElementRef;

  private filterSubscription: Subscription;
  private searchSubscription: Subscription;
  private objectSubscription: Subscription;

  /**
   * The objects for the table. This is where search terms for the table block are generated.  REQUIRED
   */
  @Input() objects;
  private data;
  sortBy: object;
  appliedFilters: Array<Filter> = [];
  searchTerm = '';

  @HostListener('window:scroll', ['$event'])
  public onScroll(event) {
    // $(window).scroll(function () {
    if (this.tableHeader) {
      const rect = this.table.nativeElement.getBoundingClientRect();
      if (rect.top <= 64 ) {
        this.renderer.setStyle(this.tableHeader.nativeElement, 'width', `${ this.table.nativeElement.offsetWidth}px`);
        this.tableHeader.nativeElement.classList.add('scrolled');
      } else {
        this.tableHeader.nativeElement.classList.remove('scrolled');
      }
    }

  }

   searchObjects(text: string): Object[] {
     this.searchTerm = text;
     this.filterCards(text);
     this.data = this.data || [];
     return this.data.filter(object => {
       let found = false;
       const values = getObjectValue(object);
       const noSearch = (text === '' || text === undefined);
       if ( noSearch || values.findIndex(i => compareValues(i, text)) > -1 ) {
         if (this.appliedFilters.length > 0) {
           const appliedValues = [false];
           this.appliedFilters.forEach(label => {
             // appliedValues.push(getValues(object).indexOf(label.value) >= 0);
             if (getValues(object).indexOf(label.value) >= 0) {
               appliedValues[0] = true;
             }
           });
           if (appliedValues.indexOf(false) < 0) {
             found = true;
           }
         } else {
           found = true;
         }
       } else {
         found = false;
       }

       return found;
     });
     this.sortObjects();
   }

  filterCards(searchTerm) {

     [].concat(this.tableRows, this.mobileCards, this.tabletCards).forEach( array => {
       if (array) {
         array.forEach( item => {
           const filter_by = (typeof item.filter_by === 'string') ? JSON.parse(item.filter_by) : item.filter_by;
           const values = getObjectValue(filter_by);
          item.hidden = true;
           const noSearch = (searchTerm === '' || searchTerm === undefined);
          if (noSearch || values.findIndex(i => compareValues(i, searchTerm)) > -1) {
            item.hidden = false;
            if (this.appliedFilters.length > 0) {
              const appliedValues = [false];
              this.appliedFilters.forEach( f => {
                // appliedValues.push(getValues(filter_by).indexOf(f.value) >= 0);
                if (getValues(filter_by).indexOf(f.value) >= 0) {
                  appliedValues[0] = true;
                }
              });

              if (appliedValues.indexOf(false) < 0) {
                item.hidden = false;
              } else {
                item.hidden = true;
              }
            }
          }

         });
       }
     });
    this.sortObjects();
  }

  cardsAreEmpty() {
    let isEmpty = true;
    [].concat(this.tableRows, this.mobileCards, this.tabletCards).forEach( array => {
      if (array) {
        array.forEach( item => {
          if (!item.hidden) {
            isEmpty = false;
          }
        });
      }
    });
    return isEmpty;
  }

  clearFilters() {
    this.filterTableService.clearFilters('#' + this.tableId);
  }

  removeFilter(filterObject) {
     this.filterTableService.addFilter('#' + this.tableId, filterObject);
  }

  ngOnDestroy(): void {
     // console.log('Destroy Table');
    this.filterTableService.clearFilters('#' + this.tableId);
    this.appliedFilters = [];

    this.filterTableService.clearSearch('#' + this.tableId);
    this.searchTerm = undefined;

    if (this.filterSubscription) {
      this.filterSubscription.unsubscribe();
    }
    if (this.objectSubscription) {
      this.objectSubscription.unsubscribe();
    }
    if (this.searchSubscription) {
    this.searchSubscription.unsubscribe();
    }
    // this.ref.detach();
  }

  ngOnInit(): void {
    this.filterTableService.tableTypeName = this.objectTypeName;
    if (!this.isList) {
      this.filterTableService.clearSearch(this.tableId);  // TODO this should presumably be '#' + this.tableId

      if (this.searchSubscription) {
        this.searchSubscription.unsubscribe();
      }

    }
    this.filterTableService.updateMobileCards('#' + this.tableId, this.mobileCards);
      if (isObservable(this.objects)) {
        this.objectSubscription = this.objects.subscribe( objects => {
          this.data = objects;
          this.trackFilters();
        });
      } else {
        this.data = this.objects;
        this.trackFilters();
      }

  }

  trackFilters() {
    this.filteredObjects = this.data;
    this.sortBy = {attributeName: undefined, direction: undefined};

    if (!this.isList) {
      // this.sortBy = {attributeName: this.tableHeaders[0]['attribute'], direction: 'DESC'};
      this.filterSubscription = this.filterTableService.appliedFilters.subscribe(filters => {
        const tableFilters = filters['#' + this.tableId];
        if (tableFilters) {
          this.appliedFilters = tableFilters || [];
          this.filteredObjects = this.searchObjects(this.searchTerm);
          this.filterCards(this.searchTerm);
          this.ref.detectChanges();
          this.filteredResults.emit(this.filteredObjects);
        }
        this.getSearchableData();
      });
    }
  }

  applySort(attributeName: string) {
     if (attributeName) {
       if ( this.sortBy[`attributeName`] && this.sortBy[`attributeName`] === attributeName) {
         if (this.sortBy[`direction`] === 'ASC') {
           this.sortBy[`direction`] = 'DESC';
         } else {
           this.sortBy[`direction`] = 'ASC';
         }
       } else {
          this.sortBy = {attributeName: attributeName, direction: 'DESC'};
       }
     }

     this.sortObjects();
  }

  sortObjects() {

     if (this.sortBy) {

       let ascending = this.sortBy[`direction`] === 'ASC';
       let sortByAttribute = this.sortBy['attributeName'];

       if (!sortByAttribute) {

         this.sortBy = {attributeName: this.tableHeaders[0]['attribute'], direction: 'DESC'};
         if (this.initialSort) {
           this.sortBy = this.initialSort;
         }
         sortByAttribute = this.tableHeaders[0]['attributeName'];
         ascending = true;
       }
       if (sortByAttribute) {
         if (this.tableRows && this.sortable) {
           const tempArry = this.tableRows.toArray().sort((a, b) => {
             const stringA = JSON.parse(a.filter_by)[sortByAttribute].toString().toLowerCase();
             const stringB = JSON.parse(b.filter_by)[sortByAttribute].toString().toLowerCase();
             if (!ascending) {
               if (stringA < stringB) {
                 return -1;
               }
               if (stringA > stringB) {
                 return 1;
               }
             } else {
               if (stringA > stringB) {
                 return -1;
               }
               if (stringA < stringB) {
                 return 1;
               }
             }
             return 0;
           });

           this.tableRows.reset(tempArry);
         }
         if (this.filteredObjects) {
           this.filteredObjects = this.filteredObjects.sort((a, b) => {
             let stringA = a[this.sortBy['attributeName']];
             let stringB = b[this.sortBy['attributeName']];
             if (typeof stringA  === 'string') {
               stringA = stringA.toString().toLowerCase();
               stringB = stringB.toString().toLowerCase();
             }
             if (!ascending) {
               if (stringA < stringB) {
                 return -1;
               }
               if (stringA > stringB) {
                 return 1;
               }
             } else {
               if (stringA > stringB) {
                 return -1;
               }
               if (stringA < stringB) {
                 return 1;
               }
             }
             return 0;
           });
         }
         this.ref.detectChanges();
       }
     }
     this.filterTableService.updateMobileCards('#' + this.tableId, this.mobileCards);
  }

  constructor(private ref: ChangeDetectorRef, private filterTableService: CloFilterTableService, private renderer: Renderer2) {  }

  getSearchableData() {
    let sd = [];
    if (this.filteredObjects && this.filteredObjects.length > 0 ) {
      getValues(this.filteredObjects).forEach( o => sd.push(getValues(o)));
      sd = [].concat.apply([], sd);
      const searchableSet = new Set(sd);
      sd = [...searchableSet].filter(Boolean);
    }

    // console.log(sd);
    this.filterTableService.setSearchableData('#' + this.tableId, sd);
  }

  ngAfterViewInit() {
    this.searchSubscription = this.filterTableService.searchFieldTerm.subscribe(value => {
      const newValue = value['#' + this.tableId];
      const clearingSearch = ((this.searchTerm !== '') && (!value || newValue === ''));
      this.searchTerm = newValue;

      this.filteredObjects = this.searchObjects(this.searchTerm);
      this.filteredResults.emit(this.filteredObjects);
      if (this.searchTerm || clearingSearch) {
        this.ref.detectChanges();
      }
    });
    this.ref.detectChanges();

    this.tableRows.changes.subscribe( change => {

      this.ref.detectChanges();
      this.sortObjects();
    });

    this.mobileCards.changes.subscribe( change => {
      this.filterTableService.updateMobileCards('#' + this.tableId, this.mobileCards);
      this.ref.detectChanges();
      // this.sortObjects();
    });
    this.tabletCards.changes.subscribe( change => {
      this.ref.detectChanges();
      this.sortObjects();
    });
  }

  rowSelected(object) {
     this.rowClicked.emit(object);
  }

  ngOnChanges(changes: SimpleChanges): void {

  }
}

function getValues(array) {
  const values = Object.keys(array).map(function(e) {
    return array[e];
  });

  return values;
}
function getObjectValue (obj) {
  let vals = [];
  for ( let key in obj ) {
    if ( obj.hasOwnProperty(key) ) {
      vals.push(obj[key]);
    }
  }
  return vals;
}

function compareValues(value, searchTerm) {
  if (value === undefined || value === null) { return false; }
  if (searchTerm === undefined || searchTerm === '') { return true; }
  return value.toString().toLowerCase().includes(searchTerm.toString().toLowerCase());
}
